import { useState } from 'react';
import { USER_STATUSES, type LocalUser, type LocalUserStatus, type Tenant } from '../../../identity/api';
import { Button, Chip, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import { client } from '../../../identity/session';
import { fmtDate, sortedByName } from '../../../identity/format';
import {
  Code,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Pager,
  ScimChip,
  EntraChip,
  TableScroller,
  TagsField,
  useAsync,
} from '../../../components/common';

const PAGE_SIZE = 25;

/**
 * Roles of a local user. Tenant roles (mapped to scopes) toggle as chips; anything else is typed as a plain
 * identity role, which grants no scope once the tenant defines roles.
 */
function RolePicker({
  tenant,
  value,
  onChange,
}: {
  tenant: Tenant;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const mapped = [...(tenant.roles ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const names = new Set(mapped.map((r) => r.name));
  const others = value.filter((r) => !names.has(r));
  const toggle = (name: string) =>
    onChange(value.includes(name) ? value.filter((r) => r !== name) : [...value, name]);
  if (!mapped.length) {
    return (
      <TagsField
        label="Roles"
        value={value}
        onChange={onChange}
        placeholder="clerk, supervisor…"
        hint="Identity roles (the singular role claim). Authority still comes from the tenant's claims."
      />
    );
  }
  return (
    <>
      <div className="nyk-ui-field" data-testid="tenant-role-picker">
        <span>Roles</span>
        <div className="nyk-inline">
          {mapped.map((r) => (
            <Chip
              key={r.id}
              size="sm"
              selected={value.includes(r.name)}
              onClick={() => toggle(r.name)}
              title={r.scopes.join(' ') || 'no scopes'}
            >
              {r.name}
            </Chip>
          ))}
        </div>
        <span className="nyk-ui-hint">Tenant roles grant scopes; a user without one gets none.</span>
      </div>
      <TagsField
        label="Other roles"
        value={others}
        onChange={(v) => onChange([...value.filter((r) => names.has(r)), ...v])}
        placeholder="identity-only roles…"
        hint="Forwarded in the role claim only."
      />
    </>
  );
}

function NewUserDialog({
  tenant,
  onCreated,
  onClose,
}: {
  tenant: Tenant;
  onCreated: (u: LocalUser) => Promise<void>;
  onClose: () => void;
}) {
  const tenantId = tenant.id;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const u = await client.users(tenantId).create({
        username: username.trim(),
        password,
        name: name.trim() || null,
        roles,
      });
      await onCreated(u);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="New user"
      maxWidth={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!username.trim() || !password}>
            Create
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="off"
          maxLength={256}
          required
          autoFocus
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          maxLength={256}
          required
        />
        <TextField
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={256}
          hint="Forwarded as the name claim."
        />
        <div className="nyk-span2">
          <RolePicker tenant={tenant} value={roles} onChange={setRoles} />
        </div>
      </div>
    </Dialog>
  );
}

function EditUserDialog({
  tenant,
  user,
  onSaved,
  onClose,
}: {
  tenant: Tenant;
  user: LocalUser;
  onSaved: () => Promise<void>;
  onClose: () => void;
}) {
  const tenantId = tenant.id;
  const [name, setName] = useState(user.name ?? '');
  const [status, setStatus] = useState<LocalUserStatus>(user.status);
  const [roles, setRoles] = useState<string[]>(user.roles.map((r) => r.role));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rolesChanged = roles.join('\n') !== user.roles.map((r) => r.role).join('\n');
  const save = async () => {
    setBusy(true);
    try {
      const api = client.users(tenantId);
      await api.update(user.id, { name: name.trim() || null, status });
      if (rolesChanged) await api.roles.replace(user.id, roles);
      await onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`User ${user.username}`}
      maxWidth={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy}>
            Save
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={256}
        />
        <Select
          label="Status"
          value={status}
          onChange={(v) => setStatus(v as LocalUserStatus)}
          options={USER_STATUSES.map((s) => ({ value: s, label: s }))}
          hint="Disabled users get 401 at sign-in."
        />
        <div className="nyk-span2">
          <RolePicker tenant={tenant} value={roles} onChange={setRoles} />
        </div>
        <div className="nyk-ui-field nyk-span2">
          <span>Id</span>
          <Code>{user.id}</Code>
        </div>
      </div>
    </Dialog>
  );
}

function PasswordDialog({
  tenantId,
  user,
  onDone,
  onClose,
}: {
  tenantId: string;
  user: LocalUser;
  onDone: () => void;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = again.length > 0 && again !== password;
  const save = async () => {
    setBusy(true);
    try {
      await client.users(tenantId).setPassword(user.id, password);
      onDone();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reset password for ${user.username}`}
      maxWidth={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!password || password !== again}>
            Set password
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="New password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          maxLength={256}
          required
          autoFocus
          fieldClassName="nyk-span2"
        />
        <TextField
          label="Repeat"
          type="password"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
          autoComplete="new-password"
          error={mismatch ? 'The passwords differ.' : undefined}
          required
          fieldClassName="nyk-span2"
        />
      </div>
    </Dialog>
  );
}

/** Local users authenticate only when the tenant's identity provider is `Database`. */
export function UsersTab({
  tenant,
  canWrite,
  notify,
}: {
  tenant: Tenant;
  canWrite: boolean;
  notify: { success: (t: string) => void; error: (t: string) => void };
}) {
  const [page, setPage] = useState(1);
  const users = useAsync(
    () => client.users(tenant.id).list({ page, pageSize: PAGE_SIZE }),
    [tenant.id, page]
  );
  const [dialog, setDialog] = useState<
    { kind: 'new' } | { kind: 'edit'; user: LocalUser } | { kind: 'password'; user: LocalUser } | null
  >(null);

  const disable = async (u: LocalUser) => {
    try {
      await client.users(tenant.id).remove(u.id);
      await users.reload();
      notify.success(`Disabled ${u.username}`);
    } catch (e) {
      notify.error((e as Error).message);
    }
  };

  const rows = sortedByName(users.data?.items ?? [], (u) => u.username);
  const mappedNames = new Set((tenant.roles ?? []).map((r) => r.name));
  return (
    <div className="nyk-card">
      <div className="nyk-card-head">
        <h2>Local users</h2>
        {canWrite && (
          <Button size="sm" onClick={() => setDialog({ kind: 'new' })}>
            New user
          </Button>
        )}
      </div>
      {tenant.identityProvider !== 'Database' && (
        <div className="nyk-notice nyk-notice-warn" data-testid="users-provider-note">
          This tenant authenticates via {tenant.identityProvider}; local users are stored but cannot sign in
          until the identity provider is switched to Database.
        </div>
      )}
      <ErrorBox error={users.error} />
      <Loading loading={users.loading} />
      <TableScroller>
        <table className="nyk-ui-table nyk-cards" data-testid="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Name</th>
              <th>Roles</th>
              <th>Status</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-user={u.id}>
                <td className="nyk-card-title">
                  <b>{u.username}</b>
                  {u.origin === 'saml' ? (
                    <EntraChip externalId={u.externalId} lastSignInAt={u.lastSignInAt} />
                  ) : (
                    u.externalId && <ScimChip externalId={u.externalId} />
                  )}
                  {u.lastSignInAt && (
                    <div className="nyk-ui-hint">last sign-in {fmtDate(u.lastSignInAt)}</div>
                  )}
                  {u.email && <div className="nyk-ui-hint">{u.email}</div>}
                  <div className="nyk-ui-hint">
                    <Code>{u.id}</Code>
                  </div>
                </td>
                <td data-label="Name">{u.name || <span className="nyk-ui-hint">—</span>}</td>
                <td data-label="Roles">
                  <div className="nyk-inline">
                    {u.roles.map((r) => (
                      <Chip
                        key={r.id}
                        size="sm"
                        selected={mappedNames.has(r.role)}
                        title={
                          !tenant.roles?.length
                            ? undefined
                            : mappedNames.has(r.role)
                              ? 'Tenant role: grants scopes'
                              : 'Not a tenant role: grants no scope'
                        }
                      >
                        {r.role}
                      </Chip>
                    ))}
                    {!u.roles.length && <span className="nyk-ui-hint">—</span>}
                  </div>
                </td>
                <td data-label="Status">
                  <Chip size="sm" selected={u.status === 'Active'}>
                    {u.status}
                  </Chip>
                </td>
                <td data-label="Updated">{fmtDate(u.updatedAt)}</td>
                <td className="nyk-row-actions">
                  {canWrite && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ kind: 'edit', user: u })}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDialog({ kind: 'password', user: u })}
                      >
                        Password
                      </Button>
                      {u.status === 'Active' && (
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Disable ${u.username}? They can no longer sign in; the account stays.`}
                          onConfirm={() => void disable(u)}
                        >
                          Disable
                        </ConfirmButton>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroller>
      {!users.loading && !rows.length && (
        <EmptyState
          title="No local users."
          hint={
            tenant.identityProvider === 'Database'
              ? 'Nobody can sign in to this tenant until a user exists.'
              : 'Only Database tenants use local users.'
          }
        />
      )}
      {users.data && (
        <Pager
          page={users.data.page}
          pageSize={users.data.pageSize}
          total={users.data.total}
          onPage={setPage}
        />
      )}
      {dialog?.kind === 'new' && (
        <NewUserDialog
          tenant={tenant}
          onCreated={async (u) => {
            await users.reload();
            notify.success(`Created ${u.username}`);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'edit' && (
        <EditUserDialog
          tenant={tenant}
          user={dialog.user}
          onSaved={async () => {
            await users.reload();
            notify.success(`Saved ${dialog.user.username}`);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'password' && (
        <PasswordDialog
          tenantId={tenant.id}
          user={dialog.user}
          onDone={() => notify.success(`Password set for ${dialog.user.username}`)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
